import React, { useState, useMemo } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { id: 'mortgage_broker', label: 'Mortgage broker / DSA', registry: 'RBI / NHB Sahay registration number' },
  { id: 'solicitor', label: 'Property lawyer / conveyancing solicitor', registry: 'Bar Council enrolment number' },
  { id: 'agent', label: 'Estate agent / RERA agent', registry: 'RERA agent registration number' },
  { id: 'surveyor', label: 'Surveyor / valuer', registry: 'MoHUA empanelled surveyor ID' },
];

const COUNTRIES = [
  { id: 'IN', label: 'India' },
  { id: 'UK', label: 'United Kingdom' },
  { id: 'CA', label: 'Canada' },
];

const ProfessionalJoinPage = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company_name: '',
    professional_type: 'mortgage_broker', country: 'IN', city: '',
    registration_number: '', linkedin_url: '', bio: '',
    consent_terms: false, consent_marketing: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const selectedType = useMemo(() => TYPES.find(t => t.id === form.professional_type) || TYPES[0], [form.professional_type]);

  useSEO({
    title: 'Join Shopperden — Mortgage Broker, Lawyer & Agent Panel',
    description: 'Apply to join Shopperden\'s vetted professional panel. Get country-matched buyer leads with verified RERA / FCA / SRA registration. India ₹2,500/yr · UK £199/yr · Canada C$249/yr.',
    canonical: '/professional/join',
  });

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm(f => ({ ...f, [k]: !f[k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_terms) { toast.error('Please accept the terms to continue'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/professional/apply`, form);
      setDone(true);
      toast.success('Application received — we\'ll review and email you within 48h');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <main className="max-w-xl mx-auto px-6 py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-bold tracking-widest uppercase text-emerald-700 mb-6">
            <Check className="h-3 w-3" /> Application received
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A]">Welcome to the panel queue</h1>
          <p className="text-slate-500 mt-4 leading-relaxed">
            Our team will verify your <strong>{selectedType.registry}</strong> against the official register and email you within 48 hours. Once approved, you'll start receiving country-matched buyer leads in {form.city || 'your city'}.
          </p>
          <a href="/" className="inline-block mt-8 px-6 py-3 bg-[#0F172A] text-white text-sm tracking-widest uppercase">
            Back to Shopperden
          </a>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-12 lg:py-20">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FEF3EC] border border-[#E07A3A]/30 rounded-full text-[11px] font-bold tracking-widest uppercase text-[#E07A3A] mb-5">
          <ShieldCheck className="h-3 w-3" /> Vetted panel — opt-in
        </div>
        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">
          Join the Shopperden professional panel
        </h1>
        <p className="text-slate-500 mt-4 leading-relaxed max-w-2xl">
          Mortgage brokers, property lawyers, RERA-registered agents and surveyors: apply once, get matched to verified buyers across the UK, India and Canada. We charge a flat annual fee — no commission, no per-lead pricing, cancel any time.
        </p>

        <form onSubmit={submit} className="bg-white border border-slate-200 mt-10 p-7 lg:p-10 rounded-lg space-y-6">
          {/* Type */}
          <div>
            <Label className="text-xs font-bold tracking-widest uppercase text-slate-700">Your role</Label>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {TYPES.map(t => (
                <button type="button" key={t.id} onClick={() => setForm(f => ({...f, professional_type: t.id}))}
                  className={`text-left px-4 py-3 border rounded-md text-sm transition-all ${form.professional_type === t.id ? 'border-[#E07A3A] bg-[#FEF3EC] ring-1 ring-[#E07A3A]/40' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`role-${t.id}`}>
                  <span className="font-medium text-[#1A1A1A]">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Country */}
          <div>
            <Label className="text-xs font-bold tracking-widest uppercase text-slate-700">Country</Label>
            <div className="flex gap-2 mt-2">
              {COUNTRIES.map(c => (
                <button type="button" key={c.id} onClick={() => setForm(f => ({...f, country: c.id}))}
                  className={`flex-1 px-4 py-2 border rounded-md text-sm transition-all ${form.country === c.id ? 'border-[#0F172A] bg-[#0F172A] text-white' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  data-testid={`country-${c.id}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Identity */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full name *</Label>
              <Input id="name" value={form.name} onChange={update('name')} required data-testid="apply-name" />
            </div>
            <div>
              <Label htmlFor="email">Work email *</Label>
              <Input id="email" type="email" value={form.email} onChange={update('email')} required data-testid="apply-email" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={update('phone')} data-testid="apply-phone" />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" value={form.city} onChange={update('city')} data-testid="apply-city" placeholder={form.country === 'IN' ? 'Mumbai' : (form.country === 'UK' ? 'London' : 'Toronto')} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="company">Firm / company name</Label>
              <Input id="company" value={form.company_name} onChange={update('company_name')} data-testid="apply-company" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="reg">{selectedType.registry} *</Label>
              <Input id="reg" value={form.registration_number} onChange={update('registration_number')} required data-testid="apply-registration" />
              <p className="text-[11px] text-slate-400 mt-1">We cross-check this against the official register before approving.</p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input id="linkedin" value={form.linkedin_url} onChange={update('linkedin_url')} placeholder="https://linkedin.com/in/..." data-testid="apply-linkedin" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="bio">Short bio (optional, max ~200 words)</Label>
              <Textarea id="bio" value={form.bio} onChange={update('bio')} rows={4} data-testid="apply-bio" />
            </div>
          </div>

          {/* Consent */}
          <div className="border-t border-slate-200 pt-5 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="apply-consent-terms">
              <input type="checkbox" checked={form.consent_terms} onChange={toggle('consent_terms')} className="mt-1 accent-[#E07A3A] h-4 w-4" required />
              <span className="text-slate-700">
                I have read and agree to Shopperden's <a href="/terms" target="_blank" className="underline">Terms of Service</a> and <a href="/privacy" target="_blank" className="underline">Privacy Notice</a>. I understand my application will be reviewed against the relevant public register before approval.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="apply-consent-marketing">
              <input type="checkbox" checked={form.consent_marketing} onChange={toggle('consent_marketing')} className="mt-1 accent-[#E07A3A] h-4 w-4" />
              <span className="text-slate-700">
                I'd like to receive periodic platform updates and matched-buyer alerts. (You can unsubscribe at any time.)
              </span>
            </label>
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-[#E07A3A] hover:bg-[#C26A30] text-white py-6 tracking-widest uppercase text-sm" data-testid="apply-submit">
            {submitting ? 'Submitting…' : 'Apply to join the panel'}
          </Button>

          <p className="text-[11px] text-slate-400 text-center">
            We review applications within 48 hours. By submitting, you consent to Shopperden processing the above details under DPDP Act 2023 (India) / UK GDPR Art. 6(1)(b) for verifying your application.
          </p>
        </form>
      </main>
    </div>
  );
};

export default ProfessionalJoinPage;
